import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Button,
} from "@mui/material";
import {
  Edit,
  DeleteOutline,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
} from "@mui/icons-material";
import SelectField from "../../atoms/SelectField/SelectField";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import { deletePage, findAllPages } from "../../../API/apiPages";
import { findAllModules } from "../../../API/apiModules";
import { SystemPage } from "../../../types/SystemPage";
import { SystemModule } from "../../../types/SystemModule";
import { useAuth } from "../../../resources/AuthProvider";
import styles from "./GestaoPaginas.module.css";
import styles_table from "../../../styles/Table.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_grid from "../../../styles/Grid.module.css";
import styles_input from "../../../styles/Input.module.css";

const GestaoPaginas = () => {
  const navigate = useNavigate();
  const { userPageList } = useAuth();
  const canAccessForm = userPageList.includes("nova-pagina");

  const [pages, setPages] = useState<SystemPage[]>([]);
  const [modules, setModules] = useState<SystemModule[]>([]);
  const [moduleFilter, setModuleFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingConfirmDialog, setLoadingConfirmDialog] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const [selectedPageName, setSelectedPageName] = useState<string>("");

  const selectedModule = useMemo(
    () => modules.find((module) => module.name === moduleFilter),
    [moduleFilter, modules],
  );

  useEffect(() => {
    loadModules();
  }, []);

  useEffect(() => {
    getPages();
  }, [currentPage, perPage, selectedModule?.id]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  const loadModules = async () => {
    try {
      const response = await findAllModules({ active: true });
      setModules(response.data || []);
    } catch (error) {
      setMsgError("Não foi possível carregar os módulos.");
    }
  };

  const getPages = async () => {
    setLoading(true);
    try {
      const response = await findAllPages(
        currentPage,
        perPage,
        selectedModule ? { module_id: selectedModule.id } : undefined,
      );
      setPages(response.pages || []);
      setTotalPages(response.pagination?.last_page || 1);
    } catch (error) {
      setMsgError("Não foi possível listar as páginas.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDelete = (page: SystemPage) => {
    setSelectedPageId(page.id);
    setSelectedPageName(page.name);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    if (!selectedPageId) return;

    try {
      setLoadingConfirmDialog(true);
      await deletePage(selectedPageId);
      setMsgSuccess("Página excluída com sucesso.");
      setOpenDelete(false);
      setSelectedPageId(null);
      setSelectedPageName("");
      await getPages();
    } catch (error) {
      setOpenDelete(false);
      setMsgError("Não foi possível excluir a página.");
    } finally {
      setLoadingConfirmDialog(false);
    }
  };

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>Gestão de Páginas</div>

        {canAccessForm && (
          <div className={styles.actionsHeader}>
            <Button
              variant="contained"
              className={styles_button.buttonSquarePrimary}
              onClick={() => navigate("/nova-pagina")}
            >
              Nova Página
            </Button>
          </div>
        )}

        {msgSuccess && (
          <div className={styles_table.message}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={styles_table.message}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        <div className={`${styles_grid.grid12} ${styles_general.mb4}`}>
          <div className={styles_grid.inputGroup4}>
            <label className={styles_input.titleInput}>Módulo</label>
            <SelectField
              label=""
              value={moduleFilter || "Todos"}
              onChange={(value) => {
                setModuleFilter(value === "Todos" ? "" : value);
                setCurrentPage(1);
              }}
              options={[
                { nome: "Todos" },
                ...modules.map((module) => ({ nome: module.name })),
              ]}
              disableTyping
              disableClearable
            />
          </div>
        </div>

        <TableContainer className={styles_table.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead className={styles_table.tableHead}>
                <TableRow>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Módulo</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Nome</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Descrição</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Rota</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" className={styles_table.textXs}>
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                )}

                {pages.map((page, index) => (
                  <TableRow
                    key={page.id}
                    className={`${styles_table.tableRow} ${index % 2 === 0 ? styles_table.tableRowEven : styles_table.tableRowOdd}`}
                  >
                    <TableCell padding="none" className={styles_table.textXs}>{page.module?.name || "-"}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{page.name}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{page.description || "-"}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{page.route}</TableCell>
                    <TableCell padding="none">
                      <div className={styles_table.blockActions}>
                        {canAccessForm && (
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Editar Página"
                            onClick={() => navigate(`/nova-pagina?id=${page.id}`)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Excluir Página"
                          onClick={() => handleOpenDelete(page)}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {pages.length > 0 && (
          <div className={`${styles_table.containerPagination} ${styles_general.mt4}`}>
            <div className={styles_table.qtdItems}>
              <div className={styles_table.labelSelect}>Resultados por página</div>
              <SelectField
                label=""
                value={perPage.toString()}
                onChange={(value) => setPerPage(Number(value))}
                options={[{ nome: "10" }, { nome: "20" }, { nome: "30" }]}
                disableClearable
                disableTyping
              />
            </div>

            <div className={styles_table.pagination}>
              <IconButton
                className={currentPage === 1 ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                aria-label="Primeira página"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                <KeyboardDoubleArrowLeftOutlined />
              </IconButton>

              <IconButton
                className={currentPage === 1 ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                aria-label="Página anterior"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <KeyboardArrowLeftOutlined />
              </IconButton>

              <span className={styles_table.pages}>Página {currentPage} de {totalPages}</span>

              <IconButton
                className={currentPage === totalPages ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                aria-label="Próxima página"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                <KeyboardArrowRightOutlined />
              </IconButton>

              <IconButton
                className={currentPage === totalPages ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                aria-label="Última página"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                <KeyboardDoubleArrowRightOutlined />
              </IconButton>
            </div>

            <div className={styles_table.setNumberPage}>
              <div className={styles_table.labelSelect}>Ir para a página</div>
              <SelectField
                label=""
                value={currentPage.toString()}
                onChange={(value) => setCurrentPage(Number(value))}
                options={Array.from({ length: totalPages }, (_, index) => ({
                  nome: String(index + 1),
                }))}
                disableTyping
              />
            </div>
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        open={openDelete}
        loading={loadingConfirmDialog}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Excluir Página"
        message={`Deseja realmente excluir a página "${selectedPageName}"?`}
        titleLoadButton="Excluindo..."
        titleButtonConfirm="Excluir"
      />
    </Card>
  );
};

export default GestaoPaginas;
